import { generatePath, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { SpecialTopicAddEditForm } from 'components/templates';
import { useJournalId } from 'hooks/useJournalId/useJournalId';
import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';
import { useJournalDetailsBreadcrumb } from 'pages/app/staff/journal/journalDetails/hooks';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { appPaths } from 'consts/paths/paths';
import { useAppMutation } from 'services/reactQuery/useAppMutation';
import {
    LessonId,
    SpecialTopicChildWork,
    SpecialTopicDTO,
    EditSpecialTopicFormFields,
    SpecialTopicId,
} from 'types';
import { ErrorDisplay, ErrorDisplayProps } from 'components/molecules';
import { journalTexts } from 'consts/text';
import { useEditForm } from 'hooks/useEditForm/useEditForm';
import { parseSpecialTopicDTOIntoSpecialTopicFormFields } from 'utils/parsers/specialTopic/parseSpecialTopicDTOIntoSpecialTopicFormFields';
import { useNavigateWithState } from 'hooks/useNavigateWithState/useNavigateWithState';
import { getChildrenWorkDataForSpecialTopic } from 'utils/getChildrenWorkDataForSpecialTopic';
import { parseEditSpecialTopicFormFieldsIntoUpdateSpecialTopicDTO } from 'utils/parsers/specialTopic/parseEditSpecialTopicFormFieldsIntoUpdateSpecialTopicDTO';
import { apiErrorCodes } from 'consts/api/apiErrorCodes';
import { institutionNonWorkingDayErrorStateConfig } from 'consts/journal/institutionNonWorkingDayErrorStateConfig';
import { useSpecialJournalTopicEditPageNetworkManage } from './hooks';

export const SpecialJournalTopicEditPage = () => {
    const { form, formInitialData, setFormData } = useEditForm<EditSpecialTopicFormFields>();
    const { journalId } = useJournalId();
    const [specialTopicLessonId, setSpecialTopicLessonId] = useState<LessonId>();
    const { displaySuccess } = useSimpleNotification();
    const { statefulNavigate } = useNavigateWithState();
    const { topicId: specialTopicIdParam } = useParams();

    useJournalDetailsBreadcrumb({
        journalId,
        journalType: 'SPECIAL',
    });

    const topicId = (specialTopicIdParam || '') as SpecialTopicId;

    const {
        data: specialTopicData,
        error: specialTopicError,
        isFetching: isSpecialTopicDataFetching,
    } = useAppQuery(
        'SPECIAL_TOPIC',
        [journalId, topicId],
        () => StaffHTTPService.specialTopics.getSpecialTopic(journalId, topicId),
        { refetchOnWindowFocus: false, retry: false },
    );

    const { data: specialAttendanceData } = useAppQuery(
        'SPECIAL_ATTENDANCE',
        [journalId, specialTopicLessonId],
        () =>
            StaffHTTPService.specialAttendance.getAttendanceByLessonId(
                journalId,
                specialTopicLessonId as LessonId,
            ),
        { enabled: !!specialTopicLessonId },
    );

    useEffect(() => {
        if (specialTopicData) {
            setSpecialTopicLessonId(specialTopicData.lesson.id);
        }
        if (specialAttendanceData && specialTopicData) {
            const { childrenAttendance } = specialAttendanceData;

            const { mergedChildrenWork, mergedNotInLessonChildrenWork } =
                getChildrenWorkDataForSpecialTopic({
                    childrenWork: specialTopicData.childrenWork,
                    notInLessonChildrenWork: specialTopicData.notInLessonChildrenWork,
                    childrenAttendance,
                });

            const newSpecialTopicData: Omit<
                SpecialTopicDTO,
                'childrenWork' | 'notInLessonChildrenWork'
            > & {
                childrenWork: SpecialTopicChildWork[];
                notInLessonChildrenWork: SpecialTopicChildWork[];
            } = {
                ...specialTopicData,
                childrenWork: mergedChildrenWork,
                notInLessonChildrenWork: mergedNotInLessonChildrenWork,
            };

            setFormData(parseSpecialTopicDTOIntoSpecialTopicFormFields(newSpecialTopicData));
        }
    }, [specialTopicData, setFormData, specialAttendanceData]);

    const navigateToTopicsPage = () => {
        statefulNavigate(
            generatePath(appPaths.app.journals.specialJournalDetails.pickedJournal.topics.base, {
                journalId,
            }),
            {
                replace: true,
            },
        );
    };

    const onEditTopicSuccess = () => {
        displaySuccess(
            journalTexts.journalDetails.groupJournal.topics.topicEditSuccessNotification,
        );
        navigateToTopicsPage();
    };

    const {
        mutate: updateSpecialTopic,
        error: editSpecialTopicError,
        isLoading: isEditSpecialTopicLoading,
    } = useAppMutation(StaffHTTPService.specialTopics.editSpecialTopic, {
        key: ['UPDATE_SPECIAL_TOPIC'],
        onSuccess: onEditTopicSuccess,
        invalidateQueryKeys: [
            ['SPECIAL_TOPICS', journalId],
            ['SPECIAL_TOPIC', journalId, topicId],
            ['SPECIAL_ATTENDANCE', journalId, specialTopicLessonId],
            ['UNASSIGNED_CHILDREN', specialTopicLessonId],
        ],
    });

    const {
        specialLessonsData,
        isSpecialLessonsDataFetching,
        isSpecialLessonsDataError,
        handleSelectFormTopicDate,
    } = useSpecialJournalTopicEditPageNetworkManage({
        journalId,
        initialTopicDate: specialTopicData?.date,
    });

    const onEditTopicFinish = (topicData: EditSpecialTopicFormFields) => {
        const parsedUpdateTopicDTO =
            parseEditSpecialTopicFormFieldsIntoUpdateSpecialTopicDTO(topicData);
        updateSpecialTopic({
            journalId,
            specialTopicId: topicId,
            updateSpecialTopicDTO: parsedUpdateTopicDTO,
        });
    };

    if (!topicId) {
        navigateToTopicsPage();
    }

    if (specialTopicError) {
        const specialTopicErrorCode =
            specialTopicError instanceof AxiosError ? specialTopicError?.response?.data?.code : '';
        const isInstitutionNonWorkingDayError =
            specialTopicErrorCode === apiErrorCodes.INSTITUTION_NON_WORKING_DAY;
        const errorDisplayConfig: ErrorDisplayProps = isInstitutionNonWorkingDayError
            ? institutionNonWorkingDayErrorStateConfig
            : {};

        return <ErrorDisplay {...errorDisplayConfig} />;
    }

    return (
        <SpecialTopicAddEditForm<EditSpecialTopicFormFields>
            mode="edit"
            form={form}
            initialValues={formInitialData}
            axiosError={editSpecialTopicError}
            isLoading={
                isEditSpecialTopicLoading ||
                isSpecialTopicDataFetching ||
                isSpecialLessonsDataFetching
            }
            onFinish={onEditTopicFinish}
            specialLessonsData={specialLessonsData}
            specialTopicLessonName={specialTopicData?.lesson.name}
            specialTopicTeacher={specialTopicData?.teacher}
            handleSelectFormTopicDate={handleSelectFormTopicDate}
            isSpecialLessonsDataFetching={isSpecialLessonsDataFetching}
            isSpecialLessonsDataError={isSpecialLessonsDataError}
        />
    );
};
